import { Options, Table } from '@ckprivate/ckf-ui';
import { Badge, useMantineTheme } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { DataTableColumn } from 'mantine-datatable';
import React, { useMemo, useState } from 'react';

import CustomTooltip from '../../../../../../components/CustomTooltip';
import { useI18n } from '../../../../../../contexts/I18nProvider';
import EndOfMonthService from '../../../../../../services/EndOfMonthService';
import {
  OpenpayeStatus,
  type OpenpayeUserResponse,
} from '../../../../../../types/api/response/endMonth';
import { milliToFullDateString } from '../../../../../../utils/format';

type Props = {
  companyId: string;
};

export default function LastSynchroOpenpaye({ companyId }: Props) {
  const { t, lang } = useI18n();
  const theme = useMantineTheme();
  const [columns] = useState<DataTableColumn<OpenpayeUserResponse>[]>([
    {
      accessor: 'dossierId',
      title: t('module.dossierOpenPaye'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'matricule_salarie',
      title: t('w.serialNumber'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'nom',
      title: t('w.name'),
      sortable: true,
      ellipsis: true,
      render: ({ nom, prenom }) => nom + ' ' + prenom,
    },
    {
      accessor: 'contrat_en_cours',
      title: t('com.feature.openPaye.activeOnOpenpaye'),
      sortable: true,
      ellipsis: true,
      render: ({ contrat_en_cours }) =>
        contrat_en_cours ? t('w.yes') : t('w.no'),
    },
    {
      accessor: 'status',
      title: t('w.status'),
      sortable: true,
      ellipsis: true,
      render: ({ status }) => (
        <CustomTooltip
          label={
            !status?.comment
              ? t(`com.feature.openPaye.comment.HFW_CREATED`)
              : t(status?.comment)
          }
        >
          <Badge
            autoContrast
            color={
              status?.status === OpenpayeStatus.OP_IMPORTED ||
              status?.status === OpenpayeStatus.OP_UPDATED ||
              status?.status === OpenpayeStatus.HFW_IMPORTED
                ? theme.colors.orange[2]
                : status?.status === OpenpayeStatus.ERROR
                ? theme.colors.red[5]
                : status?.status === OpenpayeStatus.OP_CREATED
                ? theme.colors.yellow[5]
                : theme.colors.green[5]
            }
          >
            {t(`com.feature.openPaye.${status?.status}`)}
          </Badge>
        </CustomTooltip>
      ),
    },
    {
      accessor: 'created',
      title: t('com.feature.openPaye.creationDate'),
      sortable: true,
      ellipsis: true,
      render: ({ created }) =>
        created ? milliToFullDateString(created) : t('w.unknown'),
    },
    {
      accessor: 'updated',
      title: t('com.feature.openPaye.lastDateUpdated'),
      sortable: true,
      ellipsis: true,
      render: ({ updated }) =>
        updated ? milliToFullDateString(updated) : t('w.unknown'),
    },
  ]);

  const { data, isLoading } = useQuery({
    queryKey: [`EndMonthService.getSynchroOpenpaye`, companyId],
    queryFn: () => EndOfMonthService.getSynchroOpenpaye(companyId),
  });
  const lastSynchro = useMemo(() => {
    if (!data) {
      return [];
    }
    return data;
  }, [data]);

  const [options] = useState<Options>({
    sort: { columnAccessor: '', direction: 'asc' },
    search: {
      accessor: 'nom',
      label: t('w.name'),
    },
  });

  return (
    <Table
      pinFirstColumn
      pinLastColumn
      rows={lastSynchro}
      options={options}
      columns={columns}
      lang={lang}
      fetching={isLoading}
      withTableBorder={false}
    />
  );
}
